import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { dayWords } from '../../lib/people';
import { relative } from '../../lib/when';
import { useFeature } from '../../lib/features';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/**
 * NOT SIGNED IN for N days (Settings -> Joiners & leavers; 30 unless
 * changed): the leavers nobody told IT about. Somebody with a checklist open
 * is left off - they are being dealt with. Start leaver here starts one
 * from this list (source "inactive") and opens it.
 */
export default function InactivePeople() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(null);
  // Joiners & leavers switched off (wave 6): the list still says who has
  // not signed in, but starts no checklist.
  const joinersOn = useFeature('joiners');

  const load = useCallback(() => axios.get(`${API}/people/inactive`)
    .then((r) => { setData(r.data); setProblem(''); })
    .catch((e) => { setData({ people: [], days: 30 }); setProblem(errorOf(e, 'Could not load who has not signed in.')); }), []);
  useEffect(() => { load(); }, [load]);

  const start = async (p) => {
    setBusy(p.id); setProblem('');
    try {
      const r = await axios.post(`${API}/people/runs`, { kind: 'leaver', user_id: p.id, source: 'inactive' });
      navigate(`/people/runs/${r.data.run.id}`, { state: { created: r.data.created } });
    } catch (e) {
      setProblem(errorOf(e, 'Could not start the leaver checklist.'));
      setBusy(null);
    }
  };

  const people = data?.people || [];
  return (
    <section className="pp-section" aria-label="Not signed in">
      <p className="pp-help pp-lead">
        Active accounts that have not signed in to Jarvis for {data?.days || 30} days or more, or were added that long
        ago and never have. Usually somebody who has left without anybody telling IT. Change the number of days
        in <Link to="/settings/people/joiners-leavers">Settings → Joiners &amp; leavers</Link>.
      </p>
      {problem && <p className="pp-problem" role="alert">{problem}</p>}
      {data === null ? <p className="pp-quiet">Loading…</p> : people.length === 0 ? (
        <div className="empty-state pp-empty"><p>Everybody has signed in within the last {data.days} days.</p></div>
      ) : (
        <ul className="pp-people" aria-label="People who have not signed in">
          {people.map((p) => {
            const last = p.last_active_at;
            return (
              <li key={p.id} className="pp-person-row" data-inactive={p.email}>
                <div className="pp-person-main">
                  <strong>{p.name}</strong>
                  <span className="pp-quiet">{p.email}{p.department ? ` · ${p.department}` : ''}{p.is_admin ? ' · administrator' : ''}</span>
                </div>
                <div className="pp-person-when" data-last={last ? 'seen' : 'never'}>
                  {last ? <>Last signed in <strong>{relative(last)}</strong></>
                    : <>Never signed in · added {dayWords(String(p.created_at || '').slice(0, 10))}</>}
                </div>
                {joinersOn && (
                  <button type="button" className="btn btn-small btn-secondary" disabled={busy === p.id}
                          onClick={() => start(p)} aria-label={`Start leaver checklist for ${p.name}`}>
                    {busy === p.id ? 'Starting…' : 'Start leaver'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
